import type { OnboardingTier } from "@supabase-functions/_shared/onboardingTier";
import { type BuildStatus, type GamePlatform } from "@/constants";
import type {
  DeleteStudioResponse,
  ReviewStudioRequest,
  StudioResponse,
  SubmitStudioRequest,
} from "@supabase-types/api.types";
import { apiRequest, type AuthTokenProvider, type AuthTokenRefresher } from "./apiRequest";
import {
  BUILD_METADATA_HEADER,
  type BuildUploadMetadata,
  encodeBuildUploadMetadata,
} from "./buildUploadMetadata";

export interface CreateGameInput {
  game_key: string;
  game_name: string;
  description: string;
  default_platform?: GamePlatform;
}

export interface UpdateGameInput {
  id: number;
  game_name: string;
  description: string;
  default_platform?: GamePlatform;
}

export interface ConfirmWorkingGameInput {
  id: number;
}

export type ApiConfig = {
  baseUrl: string;
  anonKey: string;
  loadAuthToken?: AuthTokenProvider;
  refreshAuthToken?: AuthTokenRefresher;
};

export type Api = ReturnType<typeof createApi>;

export function createApi({ baseUrl, anonKey, loadAuthToken, refreshAuthToken: loadRefreshedAuthToken }: ApiConfig) {
  let authToken: string = anonKey;

  const setAuthToken = (token: string) => {
    authToken = token;
  };

  const getAuthToken = () => {
    return authToken;
  };

  const getIsAuthenticated = () => {
    return authToken !== anonKey;
  };

  const getFreshAuthToken = async () => {
    if (!loadAuthToken) {
      return authToken;
    }

    const latestToken = await loadAuthToken?.();
    authToken = latestToken ?? anonKey;
    return authToken;
  };

  const getUserAuthToken = async () => {
    const latestToken = await getFreshAuthToken();
    return latestToken === anonKey ? null : latestToken;
  };

  // supabase-js serializes refreshes behind its own storage lock, so requests
  // rejected together queue rather than stampede the auth endpoint.
  const refreshAuthToken = async () => {
    if (!loadRefreshedAuthToken) {
      return null;
    }

    try {
      const refreshedToken = await loadRefreshedAuthToken();
      authToken = refreshedToken ?? anonKey;
      return refreshedToken;
    } catch {
      // Swallowed on purpose: the caller's original response is the error worth reporting.
      return null;
    }
  };

  return {
    getAuthToken,
    getFreshAuthToken,
    getUserAuthToken,
    setAuthToken,
    getIsAuthenticated,
    createGame<T = unknown>(data: CreateGameInput): Promise<T> {
      return apiRequest<T>(
        `${baseUrl}/devspace-create-game`,
        { method: "POST", body: JSON.stringify(data) },
        getFreshAuthToken,
        refreshAuthToken,
      );
    },
    updateGame<T = unknown>(data: UpdateGameInput): Promise<T> {
      return apiRequest<T>(
        `${baseUrl}/devspace-update-game`,
        { method: "PATCH", body: JSON.stringify(data) },
        getFreshAuthToken,
        refreshAuthToken,
      );
    },
    confirmWorkingGame<T = unknown>(data: ConfirmWorkingGameInput): Promise<T> {
      return apiRequest<T>(
        `${baseUrl}/devspace-game-working-confirm`,
        { method: "POST", body: JSON.stringify(data) },
        getFreshAuthToken,
        refreshAuthToken,
      );
    },
    uploadBuild(zipFile: File, metadata: Omit<BuildUploadMetadata, "zip_file_name">) {
      return apiRequest(
        `${baseUrl}/devspace-builds-upload`,
        {
          method: "POST",
          // The raw zip is the body so the edge function can stream it to disk
          // instead of buffering it; metadata rides in the header.
          body: zipFile,
          headers: {
            [BUILD_METADATA_HEADER]: encodeBuildUploadMetadata({ ...metadata, zip_file_name: zipFile.name }),
          },
        },
        // No refresher: a retry would re-send the whole archive, and the function
        // checks the token before the body streams, so a token valid at the start
        // lasts the whole request.
        getFreshAuthToken,
      );
    },
    updateBuild(data: { build_id: number; notes?: string }) {
      return apiRequest(
        `${baseUrl}/devspace-builds-update`,
        { method: "PATCH", body: JSON.stringify(data) },
        getFreshAuthToken,
        refreshAuthToken,
      );
    },
    deleteBuild(data: { build_id: number }) {
      return apiRequest(
        `${baseUrl}/devspace-builds-delete`,
        { method: "DELETE", body: JSON.stringify(data) },
        getFreshAuthToken,
        refreshAuthToken,
      );
    },
    updateBuildStatus(data: { build_id: number; status: BuildStatus }) {
      return apiRequest(
        `${baseUrl}/devspace-builds-update-status`,
        { method: "PATCH", body: JSON.stringify(data) },
        getFreshAuthToken,
        refreshAuthToken,
      );
    },
    getDevappReleases<T = unknown>() {
      return apiRequest<T>(`${baseUrl}/devapp-releases`, { method: "GET" }, getFreshAuthToken, refreshAuthToken);
    },
    uploadCoverImage(formData: FormData) {
      return apiRequest(
        `${baseUrl}/devspace-game-media-upload`,
        { method: "POST", body: formData },
        getFreshAuthToken,
        refreshAuthToken,
      );
    },
    deleteCoverImage(gameEntryId: number) {
      return apiRequest(
        `${baseUrl}/devspace-game-media-upload?game_entry_id=${gameEntryId}`,
        { method: "DELETE" },
        getFreshAuthToken,
        refreshAuthToken,
      );
    },
    createGameEntry(data: unknown) {
      return apiRequest(
        `${baseUrl}/devspace-game-entry`,
        { method: "POST", body: JSON.stringify(data) },
        getFreshAuthToken,
        refreshAuthToken,
      );
    },
    updateGameEntry(data: unknown) {
      return apiRequest(
        `${baseUrl}/devspace-game-entry`,
        { method: "PATCH", body: JSON.stringify(data) },
        getFreshAuthToken,
        refreshAuthToken,
      );
    },
    updateGameEntryCuration(data: { game_entry_id: number; onboarding_tier: OnboardingTier }) {
      return apiRequest(
        `${baseUrl}/devspace-game-entry-curation-update`,
        { method: "PATCH", body: JSON.stringify(data) },
        getFreshAuthToken,
        refreshAuthToken,
      );
    },
    deleteGameEntry(data: { game_entry_id: number }) {
      return apiRequest(
        `${baseUrl}/devspace-game-entry`,
        { method: "DELETE", body: JSON.stringify(data) },
        getFreshAuthToken,
        refreshAuthToken,
      );
    },
    submitStudio({ name, logo, remove_logo }: SubmitStudioRequest) {
      const body = new FormData();
      body.set("name", name);
      if (logo) {
        body.set("logo", logo);
      }
      if (remove_logo) {
        body.set("remove_logo", "true");
      }
      return apiRequest<StudioResponse>(
        `${baseUrl}/devspace-studio-submit`,
        { method: "POST", body },
        getFreshAuthToken,
        refreshAuthToken,
      );
    },
    reviewStudio(data: ReviewStudioRequest) {
      return apiRequest<StudioResponse>(
        `${baseUrl}/devspace-studio-review`,
        { method: "PATCH", body: JSON.stringify(data) },
        getFreshAuthToken,
        refreshAuthToken,
      );
    },
    deleteStudio() {
      return apiRequest<DeleteStudioResponse>(
        `${baseUrl}/devspace-studio-delete`,
        { method: "DELETE" },
        getFreshAuthToken,
        refreshAuthToken,
      );
    },
  };
}
